import axios from 'axios';
/* eslint-disable camelcase */
import axiosInstance, { noHeadersInstance, authInstance, localInstance } from '@/plugins/axios';
import generateInsightBrief from '@/services/insightBriefService';
import generateAIInsights from '@/services/aiInsightsService';
import apiEndpoints from '../config/endpoint';

async function getAllPaginatedResults(apiEndpoint, maxPages = 20) {
  const firstUrl = `/${apiEndpoint}`;

  // Fetch the first page to discover count and page size
  const firstResponse = await axiosInstance.get(firstUrl);
  const firstData = firstResponse.data;

  const allResults = Array.isArray(firstData?.results) ? [...firstData.results] : [];

  if (!firstData?.next || !firstData?.count || allResults.length === 0) {
    return { data: { results: allResults } };
  }

  const pageSize = allResults.length;
  const totalPages = Math.min(Math.ceil(firstData.count / pageSize), maxPages);

  if (totalPages <= 1) {
    return { data: { results: allResults } };
  }

  // Build URLs for pages 2..N and fetch ALL in parallel (not sequentially)
  const sep = firstUrl.includes('?') ? '&' : '?';
  const pageUrls = [];
  for (let page = 2; page <= totalPages; page += 1) {
    pageUrls.push(`${firstUrl}${sep}page=${page}`);
  }

  const settled = await Promise.allSettled(
    pageUrls.map((url) => axiosInstance.get(url)),
  );

  settled.forEach((result) => {
    if (result.status === 'fulfilled' && Array.isArray(result.value?.data?.results)) {
      allResults.push(...result.value.data.results);
    }
  });

  return { data: { results: allResults } };
}

const getDashboard = async () => axiosInstance.get(apiEndpoints.getDashboard);
const getDashboardById = async (id) => axiosInstance.get(apiEndpoints.getDashboardById + id);
const getUpdatedData = async (theDate) => axiosInstance.get(`${apiEndpoints.getUpdatedDataDate}?datetime=${theDate}`);
const getSingleIndicator = async (indicator) => axiosInstance.get(`${apiEndpoints.getData}?indicator=${indicator}`);
const getRequiredEndpoint = async (apiEndpoint) => axiosInstance.get(`/${apiEndpoint}`);
const getLatestDate = async () => axiosInstance.get('data/?ordering=-updated_at&size=1');
const getIndicatorsWithAvailable = async (indicatorID) => axiosInstance.get(`indicators/${indicatorID}/years_available/`);
const getIndicatorsWithPeriod = async (indicatorID, period) => axiosInstance.get(`${apiEndpoints.getData}indicator=${indicatorID}&period=${period}`);
const getAllDataSources = async () => axiosInstance.get('datasources');
const getSingleIndicatorObj = async (indicatorID) => axiosInstance.get(`indicators/${indicatorID}`);
const getSingleDataSourceObj = async (datasourcesID) => axiosInstance.get(`datasources/${datasourcesID}`);
const getSingleLocationObj = async (locationID) => axiosInstance.get(`location/${locationID}`);
const getFactors = async () => axiosInstance.get('/factors/');
const getSpecificIndicator = async () => axiosInstance.get('/datasource_specific_indicator/');
const getDashboardDatasources = async (indicatorID) => axiosInstance.get(`/dashboards/${indicatorID}/datasources/`);
const getIndicatorTableData = async (indicatorID, datasourceId, valueType, location) => axiosInstance.get(
  `${apiEndpoints.getTableData}indicator=${indicatorID}&location=${location}&datasource=${datasourceId}&ordering=-created_at&value_type=${valueType}`,
);
const getSpecificDashboard = async (dashboardName) => axiosInstance.get(`/dashboards/?name=${dashboardName}`);
const getDashboardIndicator = async (dashboardID) => axiosInstance.get(`/dashboards/${dashboardID}/indicators/`);
const getIndicatorDatasources = async (indicatorID) => axiosInstance.get(`indicators/${indicatorID}/datasources/`);
const getDataObj = async (obj) => axiosInstance.get(`data/?size=2000&indicator=${obj.indicatorID}&datasource=${obj.datasourceID}`);

const getAllDataObj = async (obj) => axiosInstance.get(
  `data/?size=2000&indicator=${obj.indicatorID}&datasource=${obj.datasourceID}&period=${obj.period}`,
);
const getNHMISData = async (obj) => axiosInstance.get(
  `data/?size=2000&indicator=${obj.indicator}&datasource=30&value_type=5&period=${obj.period}`,
);
const getAllNHMISData = async (obj) => axiosInstance.get(
  `data/?size=5000&indicator=${obj.indicator}&datasource=30&value_type=5&location=1`,
);
const getNHMISDataObj = async (obj) => axiosInstance.get(
  `data/?size=2000&indicator=${obj.indicator}&datasource=${obj.datasource}&ordering=-created_at&location=1`,
);
const getWhatsNew = async () => axiosInstance.get('news/updates/?size=1000');
const saveWhatsNew = async (data) => authInstance.post('news/updates/', data);
const updateWhatsNew = async (id, data) => authInstance.patch(`news/updates/${id}/`, data);
const deleteWhatsNew = async (id) => authInstance.delete(`news/updates/${id}/`);
const tagIndicator = async (data) => authInstance.post('tags/', data);
const getTags = async () => axiosInstance.get('tags/');
const saveCustomDashboard = async (data) => axiosInstance.post('custom-dashboard/', data);
const getSingleCustomDashboard = async (id) => axiosInstance.get(`custom-dashboard/${id}`);
const getCustomDashboard = async () => axiosInstance.get('custom-dashboard/', {
  timeout: 120000,
  disableAutoRetry: true,
});
const fetchAllDataSources = async () => axiosInstance.get('datasources/?size=100');
const fetchAllCoverageLevels = async (id) => axiosInstance.get(`datasource_specific_indicator/${id}`);
const fetchAllIndicators = async () => getAllPaginatedResults(apiEndpoints.getIndicator);
const fetchAllLocation = async () => axiosInstance.get('location/?size=1000');
const getTriangulation = async (obj) => {
  const params = new URLSearchParams({
    primary_datasource: obj.primary,
    secondary_datasource: obj.secondary,
  });

  // Add optional parameters if they exist
  if (obj.optional) params.append('third_datasource', obj.optional);
  if (obj.selectedIndicator) params.append('indicator_id', obj.selectedIndicator);
  if (obj.selectedLocation) params.append('location_id', obj.selectedLocation);

  return noHeadersInstance.get(`triangulation_dashboard/?${params.toString()}`);
};
const getFunFact = async (payload) => {
  const response = await fetch(
    'https://n8n.e4eweb.space/webhook/99f746a7-2f1b-435a-aaaa-32096bb5baca',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Webhook error ${response.status}: ${text}`);
  }

  const data = await response.json(); // ✅ await once

  return data;
};

const getAiIndicatorsSuggestions = async (payload) => {
  const response = await fetch(
    'https://n8n.e4eweb.space/webhook/0bd06292-e8c8-433b-9c2d-89ba64006845',
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    },
  );

  if (!response.ok) {
    const text = await response.text();
    throw new Error(`Webhook error ${response.status}: ${text}`);
  }

  const data = await response.json(); // ✅ await once

  return data;
};

const getDataWithPeriod = async (obj) => {
  const {
    indicator, datasource, location, value_type: valueType, period,
  } = obj || {};

  // only fire request if all required props are truthy and not just empty strings
  if (
    [indicator, datasource, location, valueType, period].every(
      (val) => val !== undefined && val !== null && val.toString().trim() !== '',
    )
  ) {
    return axiosInstance.get(
      `data/?size=3000&indicator=${indicator}&datasource=${datasource}&location=${location}&value_type=${valueType}&period=${period}`,
    );
  }

  return null;
};

// const getDataWithPeriod = async (obj) =>
//   axiosInstance.get(
//     `data/?size=3000&indicator=${obj.indicator}&datasource=${obj.datasource}&location=${obj.location}&value_type=${obj.value_type}&period=${obj.period}`
//   );
// const getZonalData = async (obj) => axiosInstance.get(`data/?size=3000&indicator=${obj.indicator}&datasource=${obj.datasource}&period=${obj.period}`);
const getZonalData = async (obj) => {
  const { indicator, datasource, period } = obj || {};

  // fire request only when all props are valid
  if (indicator && datasource && period) {
    return axiosInstance.get(
      `data/?size=3000&indicator=${indicator}&datasource=${datasource}&period=${period}`,
    );
  }

  // do nothing if validation fails
  return null;
};
const getZonalData2 = async (obj) => axiosInstance.get(
  `data/?size=3000&indicator=${obj.indicator}&datasource=${obj.datasource}&period=${obj.period}&location=${obj.location}`,
);

const getDataByDatasource = async (dataSourceID) => axiosInstance.get(`data/?size=3000&datasource=${dataSourceID}`);
const getPeriodByDatasource = async (dataSourceID) => axiosInstance.get(`period-data/?datasource=${dataSourceID}`);

const getPeriod = async (obj) => {
  const { datasource, indicator, location } = obj || {};
  const toInteger = (value) => {
    const parsed = Number(value);
    return Number.isInteger(parsed) ? parsed : null;
  };
  const params = {
    datasource: toInteger(datasource),
    indicator: toInteger(indicator),
    location: toInteger(location),
  };

  if (Object.values(params).every((val) => val !== null)) {
    return axiosInstance.get(
      `period-data/?datasource=${params.datasource}&indicator=${params.indicator}&location=${params.location}`,
    );
  }

  // do nothing if validation fails
  return null;
};

// const getPeriod = async (obj) =>
//   axiosInstance.get(
//     `period-data/?datasource=${obj.datasource}&indicator=${obj.indicator}&location=${obj.location}`
//   );
const getPeriodWithoutLocation = async (obj) => axiosInstance.get(`period-data/?datasource=${obj.datasource}&indicator=${obj.indicator}`);
const getLocations = async (obj) => {
  const { datasource, indicator } = obj;

  // Block if either contains a dash (-)
  if (String(datasource).includes('-') || String(indicator).includes('-')) {
    throw new Error('Request blocked: datasource or indicator contains a hyphen (-).');
  }

  return axiosInstance.get(`location-data/?datasource=${datasource}&indicator=${indicator}`);
};
const getIndicatorDataSources = async (indicatorID) => axiosInstance.get(`indicator-data/datasources/${indicatorID}`);
const getDataSourceIndicators = async (dataSourceID) => axiosInstance.get(`datasource-data/indicators/${dataSourceID}`);

// https://msdat-api.fmohconnect.gov.ng/api/data/?size=1000&indicator=7

const otherEndpoints = [
  apiEndpoints.getLocation,
  apiEndpoints.getIndicator,
  apiEndpoints.getLHL,
  apiEndpoints.getValueType,
  apiEndpoints.getLinks,
  apiEndpoints.getFactors,
  apiEndpoints.getDSI,
  apiEndpoints.getDataSource,
  apiEndpoints.getNhmisMonthly,
];

const getOtherEndpoint = async () => {
  const settled = await Promise.allSettled(
    otherEndpoints.map((endpoint) => {
      if (endpoint === apiEndpoints.getIndicator || endpoint === apiEndpoints.getDSI) {
        return getAllPaginatedResults(endpoint);
      }
      return getRequiredEndpoint(endpoint);
    }),
  );
  // Preserve index mapping — fulfilled returns value, rejected returns null
  // LayerService already guards against null with: if (data[index] && data[index].data ...)
  return settled.map((result) => (result.status === 'fulfilled' ? result.value : null));
};
const getDataWithValueType = async (obj) => axiosInstance.get(
  `data/?size=3000&indicator=${obj.indicator}&datasource=${obj.datasource}&location=1&value_type=${obj.value_type}`,
);
const getParentData = async (obj) => axiosInstance.get(
  `data/?size=3000&indicator=${obj.indicator}&datasource=${obj.datasource}&location=${obj.location}&period=${obj.period}`,
);

const getDashboardData = async (id, obj) => {
  if (id !== null && id !== undefined) {
    const params = new URLSearchParams({ caching: true });

    Object.entries(obj).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.append(key, value);
      }
    });

    return axiosInstance.get(`dashboard-data/${id}/?${params.toString()}`);
  }
  const params = new URLSearchParams();

  Object.entries(obj).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params.append(key, value);
    }
  });

  return axiosInstance.get(`data/?${params.toString()}`);
};

const getDashboardIndicators = async (dashboardID) => axiosInstance.get(`dashboard-data/indicators/${dashboardID}/`);

const getAIInsights = async (filters = {}) => {
  const insights = await generateAIInsights(filters);
  const selectedId = filters && filters.indicatorId;

  if (!selectedId) return { data: [] };

  const focused = (Array.isArray(insights) ? insights : [])
    .filter((item) => String(item.id) === String(selectedId))
    .slice(0, 1);

  return { data: focused };
};

export default {
  otherEndpoints,
  // getLastUpdatedDate,
  getSingleIndicator,
  getRequiredEndpoint,
  getDashboard,
  getUpdatedData,
  getOtherEndpoint,
  getLatestDate,
  getIndicatorsWithAvailable,
  getIndicatorsWithPeriod,
  getDashboardById,
  getAllDataSources,
  getSingleIndicatorObj,
  getFactors,
  getSpecificIndicator,
  getDashboardDatasources,
  getSingleDataSourceObj,
  getIndicatorTableData,
  getSpecificDashboard,
  getDashboardIndicator,
  getIndicatorDatasources,
  getDataObj,
  getAllDataObj,
  getNHMISDataObj,
  getNHMISData,
  getAllNHMISData,
  getWhatsNew,
  saveWhatsNew,
  updateWhatsNew,
  deleteWhatsNew,
  saveCustomDashboard,
  getSingleCustomDashboard,
  getCustomDashboard,
  fetchAllDataSources,
  fetchAllIndicators,
  fetchAllLocation,
  getTriangulation,
  getSingleLocationObj,
  getDataWithPeriod,
  getDataWithValueType,
  getParentData,
  getZonalData,
  getZonalData2,
  getDataByDatasource,
  getPeriodByDatasource,
  getPeriod,
  getLocations,
  getIndicatorDataSources,
  getDataSourceIndicators,
  getDashboardData,
  getPeriodWithoutLocation,
  getDashboardIndicators,
  fetchAllCoverageLevels,
  tagIndicator,
  getTags,
  getFunFact,
  getAiIndicatorsSuggestions,
  /**
   * AI Confidence & Reliability Score for Indicators
   * @param {string} id - Indicator ID
   * @param {Object} params - Query parameters (location, datasource, year)
   * @returns {Promise}
   */
  // Confidence endpoint lives on the same-origin Express server (server/index.js),
  // NOT on the external data API. Use a plain axios call with a leading slash so it
  // hits window.location.origin (Render in prod, webpack-dev-server proxy in dev).
  getIndicatorConfidence: async (id, params = {}) => axios.get(`/api/indicator/${id}/confidence`, { params }),
  getIndicatorMetadata: async (id) => axios.get(`/api/indicator/${id}/metadata`),
  getInsightBrief: async (filters) => {
    try {
      const response = await localInstance.post('/insight-brief', filters);
      return response;
    } catch (localError) {
      const brief = await generateInsightBrief(filters);
      return { data: brief };
    }
  },
  getAIInsights,
};
